import { OnlinePricingStructure } from "../../../../../interfaces/onlinePricing.interface";
import { PricingTemplate } from "../../../../../interfaces/partner.interface";
import { OnlinePricingTemplate } from "../../../../../interfaces/partnerDeal.interface";

/** The four shapes an operator picks an online price range between. A flat
 *  projection of the two axes the range is stored on — nothing here is a field
 *  of its own, and every value maps back onto a structure and a card split. */
export enum OnlineTemplateChoice {
  Legacy = "legacy",
  Default = "default",
  EeaDebitSplit = "eeaDebitSplit",
  DomesticCreditSplit = "domesticCreditSplit",
}

export interface OnlineTemplateAxes {
  structure: OnlinePricingStructure;
  cardSplit: PricingTemplate;
}

export const ONLINE_TEMPLATE_CHOICES: readonly OnlineTemplateChoice[] = [
  OnlineTemplateChoice.Legacy,
  OnlineTemplateChoice.Default,
  OnlineTemplateChoice.EeaDebitSplit,
  OnlineTemplateChoice.DomesticCreditSplit,
];

const CHOICE_AXES: Record<OnlineTemplateChoice, OnlineTemplateAxes> = {
  [OnlineTemplateChoice.Legacy]: {
    structure: OnlinePricingStructure.FourBucket,
    cardSplit: PricingTemplate.Default,
  },
  [OnlineTemplateChoice.Default]: {
    structure: OnlinePricingStructure.SixteenBucket,
    cardSplit: PricingTemplate.Default,
  },
  [OnlineTemplateChoice.EeaDebitSplit]: {
    structure: OnlinePricingStructure.SixteenBucket,
    cardSplit: PricingTemplate.EuropeanDebitSplit,
  },
  [OnlineTemplateChoice.DomesticCreditSplit]: {
    structure: OnlinePricingStructure.SixteenBucket,
    cardSplit: PricingTemplate.DomesticCreditSplit,
  },
};

const LEGACY_LABEL = "Cardholder based (legacy)";

const CHOICE_LABEL: Record<OnlineTemplateChoice, string> = {
  [OnlineTemplateChoice.Legacy]: LEGACY_LABEL,
  [OnlineTemplateChoice.Default]: "Default - Interchange fee based",
  [OnlineTemplateChoice.EeaDebitSplit]: "EEA Debit Split",
  [OnlineTemplateChoice.DomesticCreditSplit]: "Domestic Credit Split",
};

// The legacy shape prices the country's own cardholder buckets, so it is named
// after the local payment method the range carries.
const LEGACY_PREFIX: Record<OnlinePricingTemplate, string> = {
  [OnlinePricingTemplate.Default]: "NL",
  [OnlinePricingTemplate.BancontactBE]: "BE",
  [OnlinePricingTemplate.CardsOnly]: "",
};

/** Which of the four a range's axes add up to. The card split is read only on
 *  sixteen buckets: four buckets price no card group for a split to regroup. */
export const onlineTemplateChoiceFor = (
  axes: OnlineTemplateAxes
): OnlineTemplateChoice => {
  if (axes.structure !== OnlinePricingStructure.SixteenBucket)
    return OnlineTemplateChoice.Legacy;
  if (axes.cardSplit === PricingTemplate.EuropeanDebitSplit)
    return OnlineTemplateChoice.EeaDebitSplit;
  if (axes.cardSplit === PricingTemplate.DomesticCreditSplit)
    return OnlineTemplateChoice.DomesticCreditSplit;
  return OnlineTemplateChoice.Default;
};

/** The axes a chosen value writes back onto the range. */
export const onlineTemplateChoiceAxes = (
  choice: OnlineTemplateChoice
): OnlineTemplateAxes => CHOICE_AXES[choice];

/** A country carrying no local payment method has no prefix to name the legacy
 *  shape after, so it is offered unprefixed. */
export const onlineTemplateChoiceLabel = (
  choice: OnlineTemplateChoice,
  template: OnlinePricingTemplate
): string => {
  if (choice !== OnlineTemplateChoice.Legacy) return CHOICE_LABEL[choice];
  const prefix =
    LEGACY_PREFIX[template] ?? LEGACY_PREFIX[OnlinePricingTemplate.Default];
  return prefix ? `${prefix} - ${LEGACY_LABEL}` : LEGACY_LABEL;
};
